import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useMessenger } from "@/context/MessengerContext";
import { toast } from "sonner";
import { MagnifyingGlass, UserPlus, UserMinus, Coins, Trophy, ChatCircle, CaretLeft, CaretRight } from "@phosphor-icons/react";
import UserBadge from "@/components/UserBadge";

const PER_PAGE = 60;

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

export default function Discover() {
  const { openWindow } = useMessenger();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  async function load(search = q, goPage = page) {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(goPage), per_page: String(PER_PAGE) });
      if (search) params.set("search", search);
      const { data } = await api.get(`/users/browse?${params.toString()}`);
      setUsers(data.users || []);
      setPage(data.page || goPage);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load("", 1); }, []);

  async function toggleFollow(u) {
    setBusyId(u.id);
    try {
      const endpoint = u.is_following ? "unfollow" : "follow";
      const { data } = await api.post(`/users/${u.id}/${endpoint}`);
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, is_following: data.is_following } : x)));
      toast.success(data.is_following ? `Following ${u.username}` : `Unfollowed ${u.username}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <DashboardLayout>
      <div className="mb-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Community</div>
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
          Discover People
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
          Follow other advertisers to see their ads show up first in your feed.
        </p>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); load(q, 1); }}
        className="mb-6 flex items-center gap-2 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-4 py-2"
      >
        <MagnifyingGlass size={18} weight="bold" className="text-slate-400" />
        <input
          data-testid="discover-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by username or full name"
          className="flex-1 bg-transparent focus:outline-none text-sm text-slate-900 dark:text-slate-100"
        />
        <button
          type="submit"
          data-testid="discover-search-btn"
          className="rounded-full bg-blue-700 hover:bg-blue-600 text-white font-bold px-4 py-1.5 text-sm transition-colors duration-200"
        >
          Search
        </button>
      </form>

      {loading && <div className="text-sm text-slate-500">Loading people…</div>}
      {!loading && users.length === 0 && (
        <div className="text-sm text-slate-500 dark:text-slate-400">No users match your search.</div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discover-user-list">
        {users.map((u) => {
          const src = resolveImg(u.avatar_url);
          return (
            <div
              key={u.id}
              data-testid={`discover-user-${u.id}`}
              className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-start gap-3">
                <Link to={`/u/${u.id}`}>
                  {src ? (
                    <img src={src} alt={u.username} className="h-14 w-14 rounded-full object-cover" />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-lg font-black">
                      {(u.username || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 max-w-full">
                    <Link to={`/u/${u.id}`} className="font-black text-slate-900 dark:text-white text-lg hover:underline truncate">
                      {u.username}
                    </Link>
                    <UserBadge user={u} size={20} />
                  </div>
                  {u.full_name && <div className="text-sm text-slate-600 dark:text-slate-300 truncate">{u.full_name}</div>}
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Trophy size={12} weight="bold" /> {u.xp || 0} XP</span>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1"><Coins size={12} weight="bold" /> {(u.credits ?? 0).toFixed(0)}</span>
                    {u.membership && u.membership !== "Free" && (
                      <>
                        <span>•</span>
                        <span className="text-yellow-600 font-bold">{u.membership}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {u.bio && (
                <p className="text-sm text-slate-600 dark:text-slate-300 mt-3 line-clamp-2">{u.bio}</p>
              )}
              <div className="mt-4 flex items-center gap-2">
                <button
                  data-testid={`discover-follow-btn-${u.id}`}
                  onClick={() => toggleFollow(u)}
                  disabled={busyId === u.id}
                  className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full font-bold px-4 py-2 text-sm transition-colors duration-200 ${
                    u.is_following
                      ? "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-red-500 hover:text-white"
                      : "bg-blue-700 hover:bg-blue-600 text-white"
                  }`}
                >
                  {u.is_following ? (
                    <><UserMinus size={16} weight="bold" /> Following</>
                  ) : (
                    <><UserPlus size={16} weight="bold" /> Follow</>
                  )}
                </button>
                <button
                  data-testid={`discover-message-btn-${u.id}`}
                  onClick={() => openWindow(u)}
                  title="Send a message"
                  className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors duration-200"
                >
                  <ChatCircle size={18} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {(users.length > 0 || page > 1) && (
        <div className="mt-8 flex items-center justify-end">
          <div className="flex items-center gap-1.5">
            <button
              data-testid="discover-prev-page"
              disabled={page <= 1 || loading}
              onClick={() => load(q, page - 1)}
              className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 px-3 py-1.5 text-xs font-bold hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:pointer-events-none"
            >
              <CaretLeft size={12} weight="bold" /> Prev
            </button>
            <div data-testid="discover-page-indicator" className="px-3 py-1.5 text-xs font-black text-slate-700 dark:text-slate-200">
              Page {page}
            </div>
            <button
              data-testid="discover-next-page"
              disabled={page >= totalPages || loading}
              onClick={() => load(q, page + 1)}
              className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 px-3 py-1.5 text-xs font-bold hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:pointer-events-none"
            >
              Next <CaretRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
